<template>
    <div class="shift-landing" :class="{ 'is-ready': is_ready }">
        <header class="shift-header">
            <div class="company-branding">
                <v-icon color="primary" size="28">mdi-hospital-box</v-icon>
                <span class="company-name">Caring Hands Home Care</span>
            </div>
        </header>

        <template v-if="['available', 'claimed_by_other'].includes(shift_status)">
            <transition name="slide-fade-down">
                <div v-if="shift_status === 'claimed_by_other'" class="claimed-banner">
                    <v-icon color="white" small>mdi-account-check</v-icon>
                    <span>This shift has been claimed by another caregiver</span>
                </div>
            </transition>

            <div class="text-center">
                <div class="shift-badge" :class="{ 'badge-unavailable': shift_status === 'claimed_by_other' }">
                    <v-icon color="white" small>{{ shift_status === 'claimed_by_other' ? 'mdi-close' : 'mdi-clock-outline' }}</v-icon>
                    <span>{{ shift_status === 'claimed_by_other' ? 'No Longer Available' : 'Open Shift' }}</span>
                </div>
            </div>

            <h1 class="shift-title">Shift Available Tomorrow</h1>
            <p class="shift-subtitle">{{ shift_data.date }}, {{ shift_data.start_time }} - {{ shift_data.end_time }}</p>

            <div class="shift-detail-card">
                <h2 class="shift-detail-title">Patient Information</h2>
                
                <div
                    v-for="(detail, index) in shift_details"
                    :key="index"
                    class="shift-detail-row"
                >
                    <span class="shift-detail-label">{{ detail.label }}</span>
                    <span class="shift-detail-value">{{ detail.value }}</span>
                </div>
            </div>

            <div class="action-buttons">
                <v-btn
                    color="success"
                    elevation="2"
                    class="action-btn claim-btn"
                    :loading="claiming"
                    :disabled="shift_status === 'claimed_by_other'"
                    x-large
                    block
                    @click="handleClaim"
                >
                    <v-icon left>mdi-check-circle</v-icon>
                    Claim This Shift
                </v-btn>

                <v-btn
                    color="grey"
                    class="action-btn pass-btn"
                    :disabled="shift_status === 'claimed_by_other'"
                    x-large
                    block
                    outlined
                    @click="handlePass"
                >
                    <v-icon left>mdi-close</v-icon>
                    Pass On This Shift
                </v-btn>
            </div>

            <p v-if="shift_status === 'available'" class="realtime-note">
                <v-icon small color="primary">mdi-information</v-icon>
                This shift is being viewed by multiple caregivers. Claim quickly to secure it.
            </p>
        </template>

        <template v-else-if="shift_status === 'claimed'">
            <div class="status-display success-state">
                <div class="status-icon-wrapper">
                    <v-icon color="white" size="48">mdi-check-circle</v-icon>
                </div>
                <h1 class="status-title">Shift Claimed!</h1>
                <p class="status-subtitle">You've successfully claimed this shift.</p>

                <div class="confirmation-card">
                    <h3>Shift Confirmation</h3>
                    <div class="confirmation-detail">
                        <span class="label">Date</span>
                        <span class="value">{{ shift_data.date }}</span>
                    </div>
                    <div class="confirmation-detail">
                        <span class="label">Time</span>
                        <span class="value">{{ shift_data.start_time }} - {{ shift_data.end_time }}</span>
                    </div>
                    <div class="confirmation-detail">
                        <span class="label">Location</span>
                        <span class="value">{{ shift_data.location }}</span>
                    </div>
                    <div class="confirmation-detail">
                        <span class="label">Patient</span>
                        <span class="value">{{ shift_data.patient_name }}</span>
                    </div>
                </div>

                <p class="next-steps">
                    A confirmation has been sent to your phone. Please arrive 10 minutes early.
                </p>
            </div>
        </template>

        <template v-else-if="shift_status === 'claimed_by_other'">
            <div class="status-display warning-state">
                <div class="status-icon-wrapper warning">
                    <v-icon color="white" size="48">mdi-account-check</v-icon>
                </div>
                <h1 class="status-title">Shift Already Claimed</h1>
                <p class="status-subtitle">Another caregiver claimed this shift while you were viewing it.</p>
            </div>
        </template>

        <template v-else-if="shift_status === 'passed'">
            <div class="status-display neutral-state">
                <div class="status-icon-wrapper neutral">
                    <v-icon color="white" size="48">mdi-close</v-icon>
                </div>
                <h1 class="status-title">Shift Passed</h1>
                <p class="status-subtitle">You've passed on this shift. It remains available for other caregivers.</p>

                <div class="info-card">
                    <v-icon color="primary" class="mr-2">mdi-information</v-icon>
                    <p>Changed your mind? Click below to view the shift details again.</p>
                </div>

                <v-btn
                    color="primary"
                    class="mt-4"
                    outlined
                    @click="$emit('reset-web-experience')"
                >
                    <v-icon left>mdi-refresh</v-icon>
                    View Shift Again
                </v-btn>
            </div>
        </template>
    </div>
</template>

<script>
export default {
    name: 'MockShiftLanding',

    props: {
        shift_data: {
            type: Object,
            required: true
        },
        shift_status: {
            type: String,
            required: true
        }
    },

    data() {
        return {
            is_ready: false,
            claiming: false
        }
    },

    computed: {
        shift_details() {
            return [
                { label: 'Patient', value: `${this.shift_data.patient_age} year old ${this.shift_data.patient_gender}, ${this.shift_data.patient_name}` },
                { label: 'Location', value: this.shift_data.location },
                { label: 'Mobility', value: this.shift_data.mobility },
                { label: 'Cognitive Concerns', value: this.shift_data.cognitive_concerns },
                { label: 'ADL Support', value: this.shift_data.adl_support.join(', ') },
                { label: 'Medications', value: this.shift_data.medication_schedule }
            ];
        }
    },

    methods: {
        handleClaim() {
            this.claiming = true;
            setTimeout(() => {
                this.claiming = false;
                this.$emit('claim');
            }, 800);
        },

        handlePass() {
            this.$emit('pass');
        }
    },

    mounted() {
        requestAnimationFrame(() => {
            this.is_ready = true;
        });
    }
}
</script>

<style lang="scss" scoped>
.shift {
    &-landing {
        --healthcare-landing-text-color: #666;
        --healthcare-landing-claimed-color: #E53935;

        padding: 1rem;
        background: #f5f7fa;
        min-height: 570px;
        opacity: 0;
        transition: opacity 0.2s ease-in-out;

        &.is-ready {
            opacity: 1;
        }
    }

    &-header {
        margin-bottom: 1rem;
    }

    &-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        background: var(--v-primary-base);
        color: #fff;
        padding: 0.35rem 0.75rem;
        border-radius: 1rem;
        font-size: 0.7rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-bottom: 0.75rem;
        transition: background 0.2s ease;

        span {
            padding-top: 3px;
        }

        &.badge-unavailable {
            background: var(--healthcare-landing-claimed-color);
        }
    }

    &-title {
        font-size: 1.25rem;
        color: #1a1a1a;
        margin-bottom: 0.25rem;
        font-weight: 700;
        text-align: center;
    }

    &-subtitle {
        font-size: 0.9rem;
        color: var(--healthcare-landing-text-color);
        margin-bottom: 1rem;
        text-align: center;
    }
}

.company {
    &-branding {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        justify-content: center;
    }

    &-name {
        font-weight: 600;
        color: var(--v-primary-base);
        font-size: 1rem;
    }
}

.shift-detail {
    &-card {
        background: #fff;
        border-radius: 12px;
        padding: 1rem;
        margin-bottom: 1rem;
        box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    }

    &-title {
        font-size: 0.85rem;
        font-weight: 600;
        color: var(--v-primary-base);
        margin-bottom: 0.5rem;
        padding-bottom: 0.5rem;
        border-bottom: 1px solid #eee;
    }

    &-row {
        display: flex;
        justify-content: space-between;
        padding: 0.5rem 0;
        border-bottom: 1px solid #f5f5f5;

        &:last-child {
            border-bottom: none;
        }
    }

    &-label {
        font-size: 0.8rem;
        color: #888;
        font-weight: 500;
    }

    &-value {
        font-size: 0.8rem;
        color: #333;
        font-weight: 500;
        text-align: right;
        max-width: 60%;
    }
}

.action-buttons {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1rem;

    .action-btn {
        border-radius: 8px !important;
        text-transform: none !important;
        font-weight: 600 !important;
        letter-spacing: 0 !important;

        &[disabled] {
            display: none;
        }
    }

    .claim-btn {
        background: linear-gradient(135deg, var(--v-success-base) 0%, #2E7D32 100%) !important;
    }
}

.claimed-banner {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    background: var(--healthcare-landing-claimed-color);
    color: white;
    padding: 0.6rem 1rem;
    margin: -0.5rem -1rem 1rem -1rem;
    font-size: 0.8rem;
    font-weight: 500;
}

.status {
    &-display {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        padding: 1.5rem 0.5rem;
    }

    &-icon-wrapper {
        width: 80px;
        height: 80px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 1rem;
        background: var(--v-success-base);

        &.warning {
            background: var(--healthcare-landing-claimed-color);
        }

        &.neutral {
            background: #9E9E9E;
        }
    }

    &-title {
        font-size: 1.25rem;
        font-weight: 700;
        color: #1a1a1a;
        margin-bottom: 0.5rem;
    }

    &-subtitle {
        font-size: 0.9rem;
        color: var(--healthcare-landing-text-color);
        margin-bottom: 1.5rem;
    }
}

.confirmation {
    &-card {
        background: #fff;
        border-radius: 12px;
        padding: 1rem;
        width: 100%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        text-align: left;

        h3 {
            font-size: 0.85rem;
            font-weight: 600;
            color: var(--v-primary-base);
            margin-bottom: 0.75rem;
            padding-bottom: 0.5rem;
            border-bottom: 1px solid #eee;
        }
    }

    &-detail {
        display: flex;
        justify-content: space-between;
        padding: 0.4rem 0;

        .label {
            font-size: 0.8rem;
            color: #888;
        }

        .value {
            font-size: 0.8rem;
            color: #333;
            font-weight: 500;
        }
    }
}

.realtime-note {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.75rem;
    color: var(--healthcare-landing-text-color);
    background: #e3f2fd;
    padding: 0.75rem;
    border-radius: 8px;
    line-height: 1.4;
}

.next-steps {
    font-size: 0.8rem;
    color: var(--healthcare-landing-text-color);
    margin-top: 1rem;
    padding: 0.75rem;
    background: #e8f5e9;
    border-radius: calc(var(--border-radius-default) * 2);
    line-height: 1.4;
}

.info-card {
    display: flex;
    align-items: flex-start;
    background: #fff;
    border-radius: calc(var(--border-radius-default) * 2);
    padding: 1rem;
    text-align: left;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);

    p {
        margin: 0;
        font-size: 0.85rem;
        color: var(--healthcare-landing-text-color);
        line-height: 1.5;
    }
}
</style>
